import { FilterType } from "components/OrderSearch/filterType";
import ChipDropdownMenu, { ChipDropdownMenuProps } from "components/OrderSearch/ChipDropdownMenu";
import { Dispatch, SetStateAction, useCallback, useMemo } from "react";
import { displayDesigner } from "utils/order";
import { SaveDraftFilters } from "components/NewOrders/SaveDraft/SaveDraftTable/SaveDraftTable.styles";
import { models } from "types/api/viewModels.ts";
import { CustomQuoteStatus } from "types/declarations/enums.ts";
import { formatQuoteStatus } from "./customQuoteUtils.ts";
import { QuoteFilterOpts } from "pages/CustomQuote/CustomQuoteLandingPage/CustomQuoteLandingPage.tsx";

interface QuoteFilterDetails extends ChipDropdownMenuProps {
	dataTestId: string;
	isVisible: boolean;
	GTMIdentifier: string;
}

interface CustomQuoteTableFiltersProps {
	filterOptions: QuoteFilterOpts;
	setFilterOptions: Dispatch<SetStateAction<QuoteFilterOpts>>;
	quotes: models["CustomQuoteViewModel"][];
	tabValue: number;
}

const CustomQuoteTableFilters = ({
	filterOptions,
	setFilterOptions,
	quotes,
	tabValue
}: CustomQuoteTableFiltersProps) => {
	const designerNumbers: string[] = useMemo(
		() =>
			Array.from(
				new Set(
					quotes
						.flatMap((quote) => displayDesigner(quote.designer))
						.filter((designer): designer is string => typeof designer === "string" && !!designer)
				)
			),
		[quotes]
	);

	const productLines: string[] = useMemo(
		() =>
			Array.from(
				new Set(
					quotes
						.map((quote) => quote?.globals?.productLine?.description)
						.filter(
							(productLine): productLine is string => typeof productLine === "string" && !!productLine
						)
				)
			),
		[quotes]
	);

	const statusOptions: string[] = useMemo(
		() =>
			Array.from(
				new Set(
					quotes
						.map((quote) => quote?.status)
						.filter(
							(status): status is models["CustomQuoteStatus"] => !!status && status in CustomQuoteStatus
						)
						.map((status) => formatQuoteStatus(status))
				)
			),
		[quotes]
	);

	const onFilterChange = useCallback(
		(id: string, values: string[]) => {
			setFilterOptions((prev: QuoteFilterOpts) => ({
				...prev,
				[id]: values
			}));
		},
		[setFilterOptions]
	);

	const quoteFilterDetails: QuoteFilterDetails[] = useMemo(
		() => [
			{
				selectedItems: filterOptions?.designers,
				onChange: (v) => onFilterChange("designers", v),
				filterType: FilterType.QUOTE_DESIGNER,
				items: designerNumbers ?? [],
				dataTestId: "all-designers-filter-chip",
				isSearchable: true,
				hasWarning: false,
				children: undefined,
				isVisible: true,
				GTMIdentifier: "custom-quote-table-designer-filter-chip"
			},
			{
				selectedItems: filterOptions?.productLines,
				onChange: (v) => onFilterChange("productLines", v),
				filterType: FilterType.QUOTE_PRODUCT_LINE,
				items: productLines ?? [],
				dataTestId: "all-product-lines-filter-chip",
				isSearchable: true,
				hasWarning: false,
				children: undefined,
				isVisible: true,
				GTMIdentifier: "custom-quote-table-product-line-filter-chip"
			},
			{
				selectedItems: filterOptions?.status,
				onChange: (v) => onFilterChange("status", v),
				filterType: FilterType.QUOTE_STATUS,
				items: statusOptions ?? [],
				dataTestId: "order-status-filter-chip",
				isSearchable: false,
				hasWarning: false,
				children: undefined,
				isVisible: tabValue === 0, // Only show Status filter on Open Requests tab
				GTMIdentifier: "custom-quote-table-status-filter-chip"
			}
		],
		[
			filterOptions?.designers,
			filterOptions?.productLines,
			filterOptions?.status,
			designerNumbers,
			productLines,
			statusOptions,
			onFilterChange,
			tabValue
		]
	);

	return (
		<SaveDraftFilters>
			{quoteFilterDetails?.map(
				(filter) =>
					filter.isVisible && (
						<ChipDropdownMenu
							key={filter.dataTestId}
							selectedItems={filter.selectedItems}
							onChange={filter.onChange}
							filterType={filter.filterType}
							items={filter.items}
							dataTestId={filter.dataTestId}
							hasWarning={filter.hasWarning}
							isSearchable={filter.isSearchable}
							GTMIdentifier={filter.GTMIdentifier}
						>
							{filter.children}
						</ChipDropdownMenu>
					)
			)}
		</SaveDraftFilters>
	);
};

export default CustomQuoteTableFilters;
